/**
 * Utilitaire pour convertir les formules LaTeX en texte simple lisible
 * Utilisé pour les prévisualisations mobiles
 */

/**
 * Convertit une formule LaTeX en texte simple
 * @param latex - Code LaTeX à convertir
 * @returns Texte simple lisible
 */
export function latexToSimpleText(latex: string): string {
  if (!latex) return '';

  let text = latex;

  // Supprimer les environnements de document
  text = text.replace(/\\begin\{document\}/g, '');
  text = text.replace(/\\end\{document\}/g, '');
  text = text.replace(/\\documentclass\{.*?\}/g, '');
  text = text.replace(/\\usepackage\{.*?\}/g, '');

  // Supprimer les environnements mathématiques
  text = text.replace(/\\\[/g, '');
  text = text.replace(/\\\]/g, '');
  text = text.replace(/\$\$/g, '');
  text = text.replace(/\$/g, '');

  // Fractions
  text = text.replace(/\\frac\{([^}]*)\}\{([^}]*)\}/g, '($1/$2)');

  // Puissances et indices
  text = text.replace(/\^\{([^}]*)\}/g, '^$1');
  text = text.replace(/_\{([^}]*)\}/g, '_$1');
  text = text.replace(/\^(\w)/g, '^$1');
  text = text.replace(/_(\w)/g, '_$1');

  // Racines
  text = text.replace(/\\sqrt\[(\d+)\]\{([^}]*)\}/g, '($2)^(1/$1)'); // Racine n-ième
  text = text.replace(/\\sqrt\{([^}]*)\}/g, '√($1)');

  // Intégrales
  text = text.replace(/\\int_\{([^}]*)\}\^\{([^}]*)\}/g, '∫[$1→$2]');
  text = text.replace(/\\int/g, '∫');

  // Sommes et produits
  text = text.replace(/\\sum_\{([^}]*)\}\^\{([^}]*)\}/g, 'Σ[$1→$2]');
  text = text.replace(/\\sum/g, 'Σ');
  text = text.replace(/\\prod_\{([^}]*)\}\^\{([^}]*)\}/g, '∏[$1→$2]');
  text = text.replace(/\\prod/g, '∏');

  // Limites
  text = text.replace(/\\lim_\{([^}]*)\}/g, 'lim[$1]');
  text = text.replace(/\\lim/g, 'lim');

  // Dérivées
  text = text.replace(/\\frac\{d([^}]*)\}\{d([^}]*)\}/g, 'd$1/d$2');
  text = text.replace(/\\partial/g, '∂');

  // Vecteurs et matrices
  text = text.replace(/\\vec\{([^}]*)\}/g, '→$1');
  text = text.replace(/\\overrightarrow\{([^}]*)\}/g, '→$1');
  text = text.replace(/\\begin\{pmatrix\}/g, '[');
  text = text.replace(/\\end\{pmatrix\}/g, ']');
  text = text.replace(/\\begin\{bmatrix\}/g, '[');
  text = text.replace(/\\end\{bmatrix\}/g, ']');
  text = text.replace(/\\begin\{matrix\}/g, '[');
  text = text.replace(/\\end\{matrix\}/g, ']');
  text = text.replace(/\\\\/g, '; ');
  text = text.replace(/&/g, ', ');

  // Lettres grecques
  const greekLetters: Record<string, string> = {
    'alpha': 'α', 'beta': 'β', 'gamma': 'γ', 'delta': 'δ', 'epsilon': 'ε',
    'zeta': 'ζ', 'eta': 'η', 'theta': 'θ', 'iota': 'ι', 'kappa': 'κ',
    'lambda': 'λ', 'mu': 'μ', 'nu': 'ν', 'xi': 'ξ', 'pi': 'π',
    'rho': 'ρ', 'sigma': 'σ', 'tau': 'τ', 'upsilon': 'υ', 'phi': 'φ',
    'chi': 'χ', 'psi': 'ψ', 'omega': 'ω',
    'Gamma': 'Γ', 'Delta': 'Δ', 'Theta': 'Θ', 'Lambda': 'Λ', 'Xi': 'Ξ',
    'Pi': 'Π', 'Sigma': 'Σ', 'Phi': 'Φ', 'Psi': 'Ψ', 'Omega': 'Ω'
  };

  Object.entries(greekLetters).forEach(([latex, unicode]) => {
    text = text.replace(new RegExp(`\\\\${latex}\\b`, 'g'), unicode);
  });

  // Symboles mathématiques
  const mathSymbols: Record<string, string> = {
    'infty': '∞',
    'pm': '±',
    'mp': '∓',
    'times': '×',
    'div': '÷',
    'cdot': '·',
    'leq': '≤',
    'geq': '≥',
    'neq': '≠',
    'approx': '≈',
    'equiv': '≡',
    'sim': '∼',
    'propto': '∝',
    'in': '∈',
    'notin': '∉',
    'subset': '⊂',
    'subseteq': '⊆',
    'supset': '⊃',
    'supseteq': '⊇',
    'cup': '∪',
    'cap': '∩',
    'emptyset': '∅',
    'forall': '∀',
    'exists': '∃',
    'nabla': '∇',
    'angle': '∠',
    'triangle': '△',
    'parallel': '∥',
    'perp': '⊥',
    'rightarrow': '→',
    'leftarrow': '←',
    'leftrightarrow': '↔',
    'Rightarrow': '⇒',
    'Leftarrow': '⇐',
    'Leftrightarrow': '⇔',
  };

  Object.entries(mathSymbols).forEach(([latex, unicode]) => {
    text = text.replace(new RegExp(`\\\\${latex}\\b`, 'g'), unicode);
  });

  // Fonctions mathématiques
  text = text.replace(/\\sin/g, 'sin');
  text = text.replace(/\\cos/g, 'cos');
  text = text.replace(/\\tan/g, 'tan');
  text = text.replace(/\\log/g, 'log');
  text = text.replace(/\\ln/g, 'ln');
  text = text.replace(/\\exp/g, 'exp');
  text = text.replace(/\\min/g, 'min');
  text = text.replace(/\\max/g, 'max');

  // Accolades et parenthèses
  text = text.replace(/\\left\(/g, '(');
  text = text.replace(/\\right\)/g, ')');
  text = text.replace(/\\left\[/g, '[');
  text = text.replace(/\\right\]/g, ']');
  text = text.replace(/\\left\{/g, '{');
  text = text.replace(/\\right\}/g, '}');
  text = text.replace(/\\left\|/g, '|');
  text = text.replace(/\\right\|/g, '|');

  // Espaces et formatage
  text = text.replace(/\\quad/g, '  ');
  text = text.replace(/\\qquad/g, '    ');
  text = text.replace(/\\,/g, ' ');
  text = text.replace(/\\;/g, ' ');
  text = text.replace(/\\ /g, ' ');
  text = text.replace(/~/g, ' ');

  // Texte et formatage
  text = text.replace(/\\text\{([^}]*)\}/g, '$1');
  text = text.replace(/\\textbf\{([^}]*)\}/g, '$1');
  text = text.replace(/\\textit\{([^}]*)\}/g, '$1');
  text = text.replace(/\\mathbf\{([^}]*)\}/g, '$1');

  // Sections et structures (pour le contenu de cours)
  text = text.replace(/\\section\{([^}]*)\}/g, '\n\n=== $1 ===\n');
  text = text.replace(/\\subsection\{([^}]*)\}/g, '\n\n--- $1 ---\n');
  text = text.replace(/\\subsubsection\{([^}]*)\}/g, '\n\n• $1\n');
  text = text.replace(/\\paragraph\{([^}]*)\}/g, '\n$1: ');

  // Environnements de liste
  text = text.replace(/\\begin\{itemize\}/g, '\n');
  text = text.replace(/\\end\{itemize\}/g, '\n');
  text = text.replace(/\\begin\{enumerate\}/g, '\n');
  text = text.replace(/\\end\{enumerate\}/g, '\n');
  text = text.replace(/\\item/g, '\n• ');

  // Environnements mathématiques avancés
  text = text.replace(/\\begin\{equation\*?\}/g, '\n');
  text = text.replace(/\\end\{equation\*?\}/g, '\n');
  text = text.replace(/\\begin\{align\*?\}/g, '\n');
  text = text.replace(/\\end\{align\*?\}/g, '\n');

  // Supprimer les commandes LaTeX restantes
  text = text.replace(/\\[a-zA-Z]+\*?/g, '');

  // Nettoyer les accolades restantes
  text = text.replace(/\{/g, '');
  text = text.replace(/\}/g, '');

  // Nettoyer les espaces multiples et les sauts de ligne
  text = text.replace(/\n{3,}/g, '\n\n');
  text = text.replace(/[ \t]{2,}/g, ' ');
  text = text.trim();

  return text;
}
